import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconUpload, IconPdf, IconClose } from '../../icons';
import { ClipLoader } from 'react-spinners';
import { ParsedZugferdData } from '../../types/receivedInvoiceTypes';
import { useReceivedInvoiceUpload } from './useReceivedInvoiceUpload';
import type { ReceivedInvoiceUploadMeta } from './useReceivedInvoiceUpload';

// Re-export the meta type so existing consumers
// (`import { ReceivedInvoiceUploadMeta } from '.../ReceivedInvoiceUpload'`)
// keep working after the logic moved into `useReceivedInvoiceUpload`.
export type { ReceivedInvoiceUploadMeta } from './useReceivedInvoiceUpload';

interface ReceivedInvoiceUploadProps {
  onUploadSuccess: (data: ParsedZugferdData, meta: ReceivedInvoiceUploadMeta) => void;
  /**
   * Loop-A weiche (§5.11): called instead of `onUploadSuccess` when
   * `INBOUND_ENABLED` is on and the file was ingested via the server WORM
   * pipeline. `deferred:true` means archived-but-parse-postponed (fair use).
   * Optional — the flag-off build never calls it.
   */
  onInboundUploadSuccess?: (result: { inboundDocumentId: string; deferred: boolean }) => void;
}

/**
 * Full-width ZUGFeRD/XRechnung upload dropzone. The Loop-A upload weiche and
 * all async processing now live in `useReceivedInvoiceUpload`; this component
 * owns only the dropzone UI. Its public API and DOM are unchanged (parity
 * gate for the three `ReceivedInvoiceUpload.*.test.tsx` suites).
 */
const ReceivedInvoiceUpload: React.FC<ReceivedInvoiceUploadProps> = ({
  onUploadSuccess,
  onInboundUploadSuccess,
}) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { processFile, loading, error, accept } = useReceivedInvoiceUpload({
    onUploadSuccess,
    onInboundUploadSuccess,
  });

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void processFile(file);
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processFile(file);
    // Reset so re-selecting the same file re-fires `onChange`.
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="mb-6">
      <div
        onClick={() => !loading && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
          ${isDragging
            ? 'border-indigo-500 bg-[var(--tone-accent-bg)]'
            : 'border-strong hover:border-indigo-500 hover:bg-[rgb(var(--surface-raised-rgb)/0.5)]'
          }
          ${loading ? 'cursor-default opacity-75' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleChange}
          disabled={loading}
        />

        {loading ? (
          <div className="flex flex-col items-center gap-3">
            <ClipLoader color="var(--accent)" size={36} />
            <p className="text-sm text-accent-ink font-medium">
              {t('receivedInvoices.loadingParse', 'Lese ZUGFeRD-Daten...')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted">
            <div className="flex items-center gap-2">
              <IconUpload size={30} className="text-accent-ink" />
              <IconPdf size={24} className="text-[var(--tone-danger-fg)]" />
            </div>
            <div>
              <p className="text-primary font-medium">
                {t('receivedInvoices.uploadTitle', 'ZUGFeRD / XRechnung PDF hochladen')}
              </p>
              <p className="text-sm mt-1">
                {t('receivedInvoices.uploadHint', 'PDF hier ablegen oder klicken zum Auswählen')}
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">
          <IconClose size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ReceivedInvoiceUpload;
